import './preprocessHeroPaths';
import { useCallback, useMemo, useEffect } from 'react';
import { InstancedFlow2 as InstancedFlow } from '../../common/CurveModifier2';
import { useAsset } from '@artica/assets';
import { Visible } from '@immutabl3/alchemy';
import { parallax } from '@immutabl3/alchemy/three';
import offsets from './heroPaths.paths.animations.json';
import animation from './heroPaths.animation.json';
import {
  Color,
  Vector3,
  CurvePath,
  DoubleSide,
  MeshStandardMaterial,
  CatmullRomCurve3,
  AdditiveBlending,
} from 'three';

const HeroPathOffsetter = ({
  index,
  animation,
  flowHero,
}) => {
  const onAnimation = useCallback((_, anim) => {
    const { animations } = anim;
    const [{ currentValue: offset }] = animations;
    flowHero.moveIndividualAlongCurve(index, offset);
  }, [index, flowHero]);

  return (
    <parallax.group
      beaker="chapter1"
      animation={ animation }
      onAnimation={ onAnimation }
    />
  );
};

const HeroPaths = function({ paths }) {
  const pathextrude = useAsset('path_extrude_2');
  const extrudemap = useAsset('extrude_tex');
  const material = useMemo(() => {
    return new MeshStandardMaterial({
      emissiveMap: extrudemap,
      emissive: new Color(0.12, 0.15, 0.18),
      color: new Color(0, 0, 0),
      blending: AdditiveBlending,
      side: DoubleSide,
      fog: false,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      opacity: 1,
      toneMapped: false,
    });
  }, [extrudemap]);

  const flowHero = useMemo(() => {
    const { geometry } = pathextrude.nodes.Sweep2;
    const instanceFlow = new InstancedFlow(5, 5, geometry, material);
    return instanceFlow;
  }, [pathextrude.nodes.Sweep2, material]);

  const curves = useMemo(() => {
    const curves = new CurvePath();

    for (const line of paths) {
      const geo = line.geometry.attributes.position;
      const pts = [];
      for (let i = 0; i < geo.count; i++) {
        pts.push(new Vector3().fromBufferAttribute(geo, i));
      }
      
      curves.add(new CatmullRomCurve3(pts, false));
    }

    return curves;
  }, [paths]);

  useEffect(() => {
    curves.curves.forEach((curve, idx) => {
      flowHero.updateCurve(idx, curve);
      flowHero.setCurve(idx, idx);
    });
  }, [flowHero, curves]);

  return (
    <>
      { offsets.map((animation, idx) => (
        <HeroPathOffsetter
          key={ idx }
          index={ idx }
          animation={ animation }
          flowHero={ flowHero }
        />
      )) }
      <parallax.primitive
        beaker="chapter1"
        animation={ animation }
        object={ flowHero.object3D }
      />
    </>
  );
};

export default function HeroPathsContainer({ paths }) {
  return (
    <Visible beaker="chapter1" animation={ animation }>
      <HeroPaths paths={ paths } />
    </Visible>
  );
};